import { createReducer } from '@reduxjs/toolkit';

import * as bookingActions from './bookingActions.js';

const initialState = {
  requesting: false,
  error: null,

  id: 0,
  uid: '',
  sid: 0,
  type: 'booking',
  isActive: false,
  isAdded: false,

  // ===== CONTENT =====
  heading: {
    id: 0,
    blockSid: 0,
    title: '',
    titleColor: '#000000',
    description: '',
    descriptionColor: '#333333',
    image: null,
    textAlign: 'left',
    divider: null,
    position: 'top',
    layout: null,
  },

  view: 'list',
  layout: 'cards',
  items: [],

  rentalType: '',
  activeItem: null,

  // ===== DESIGN =====
  containerDesign: {
    isBackground: true,
    backgroundColor: '#FFFFFF',
    textColor: '#000000',
    border: 'soft',
    shadow: null,
    padding: 12,
    margin: 15,
  },

  itemDesign: {
    backgroundColor: '#F5F5F5',
    textColor: '#000000',
    border: 'soft',
    shadow: 'none',
  },

  buttonDesign: {
    backgroundColor: null,
    textColor: null,
    opacity: null,
    border: null,
    shadow: null,
    shape: null,
    text: null,
    height: null,
  },

  designView: 'list',
  designLayout: 'card',
  itemTextAlign: null,
  itemTitleSize: null,
  itemLayout: null,
  itemFields: null,
  itemColumns: null,
};

const booking = createReducer(initialState, (builder) => {
  builder
    .addCase(bookingActions.bookingUpdateId, (state, { payload: id }) => ({ ...state, id }))
    .addCase(bookingActions.bookingUpdateUID, (state, { payload: uid }) => ({ ...state, uid }))
    .addCase(bookingActions.bookingUpdateSID, (state, { payload: sid }) => ({ ...state, sid }))
    .addCase(bookingActions.bookingUpdateIsAdded, (state, { payload: isAdded }) => ({ ...state, isAdded }))
    .addCase(bookingActions.bookingUpdateIsActive, (state, { payload: isActive }) => ({ ...state, isActive }))
    .addCase(bookingActions.bookingUpdateRentalType, (state, { payload: rentalType }) => ({ ...state, rentalType }))

    .addCase(bookingActions.bookingStore, (state, { payload }) => {
      const { id, uid, sid, isActive, isAdded, data } = payload ?? {};

      const blockContent = data?.blockContent ?? {};
      const blockDesign = data?.blockDesign ?? {};

      const heading = blockContent?.heading ?? {};
      const items = blockContent?.items ?? [];

      const containerDesign = blockDesign?.containerDesign ?? {};
      const itemDesign = blockDesign?.itemDesign ?? {};
      const buttonDesign = blockDesign?.buttonDesign ?? {};

      return {
        ...state,
        requesting: false,
        error: null,

        id: id ?? 0,
        uid: uid ?? '',
        sid: sid ?? 0,
        isActive: Boolean(isActive),
        isAdded: Boolean(isAdded),

        heading: {
          ...state.heading,
          ...heading,
          title: heading?.title ?? state.heading.title,
          description: heading?.description ?? state.heading.description,
          textAlign: heading?.textAlign ?? state.heading.textAlign,
        },

        view: blockContent?.view ?? state.view,
        layout: blockContent?.layout ?? state.layout,
        items,

        // для нового блока, если backend вернул rentalType: null,
        // не подтягиваем старое значение из предыдущего блока
        rentalType: blockContent?.rentalType ?? initialState.rentalType,

        containerDesign: { ...state.containerDesign, ...containerDesign },
        itemDesign: {
          ...state.itemDesign,
          ...itemDesign,
        },
        buttonDesign: { ...state.buttonDesign, ...buttonDesign },

        designView: blockDesign?.view ?? state.designView,
        designLayout: blockDesign?.layout ?? state.designLayout,
        itemTextAlign: blockDesign?.itemTextAlign ?? state.itemTextAlign,
        itemTitleSize: blockDesign?.itemTitleSize ?? state.itemTitleSize,
        itemLayout: blockDesign?.itemLayout ?? state.itemLayout,
        itemFields: blockDesign?.itemFields ?? state.itemFields,
        itemColumns: blockDesign?.itemColumns ?? state.itemColumns,
      };
    })

    .addCase(bookingActions.bookingClear, () => ({ ...initialState }))

    .addCase(bookingActions.bookingUpdateRequest, (state) => ({ ...state, requesting: true, error: null }))
    .addCase(bookingActions.bookingUpdateIsLoading, (state, { payload }) => {
      state.requesting = payload;
    })
    .addCase(bookingActions.bookingUpdateError, (state, { payload }) => ({ ...state, error: payload, requesting: false }))
    .addCase(bookingActions.bookingUpdateStore, (state) => ({ ...state, error: null, requesting: false }))

    .addCase(bookingActions.bookingUpdateItems, (state, { payload: items }) => ({ ...state, items }))
    .addCase(bookingActions.bookingUpdateActiveItem, (state, { payload: activeItem }) => ({ ...state, activeItem }))
    .addCase(bookingActions.bookingUpdateHeading, (state, { payload: heading }) => ({ ...state, heading: { ...state.heading, ...heading } }))
    .addCase(bookingActions.bookingUpdateBlockContent, (state, { payload }) => ({
      ...state,
      ...payload,
    }))
    .addCase(bookingActions.bookingUpdateBlockDesign, (state, { payload }) => {
      const { containerDesign, itemDesign, buttonDesign, view, layout, itemTextAlign, itemTitleSize, itemLayout, itemFields, itemColumns } = payload ?? {};

      if (containerDesign) {
        state.containerDesign = {
          ...state.containerDesign,
          ...containerDesign,
        };
      }

      if (itemDesign) {
        state.itemDesign = {
          ...state.itemDesign,
          ...itemDesign,
        };
      }

      if (buttonDesign) {
        state.buttonDesign = {
          ...state.buttonDesign,
          ...buttonDesign,
        };
      }

      if (view !== undefined) state.designView = view;
      if (layout !== undefined) state.designLayout = layout;
      if (itemTextAlign !== undefined) state.itemTextAlign = itemTextAlign;
      if (itemTitleSize !== undefined) state.itemTitleSize = itemTitleSize;
      if (itemLayout !== undefined) state.itemLayout = itemLayout;
      if (itemFields !== undefined) state.itemFields = itemFields;
      if (itemColumns !== undefined) state.itemColumns = itemColumns;
    });
});

export default booking;
